"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import type { UNICODE_TEXT_NORMALIZER_STRINGS } from "@/components/tools/unicode-text-normalizer";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type ChangeReport,
  codePointList,
  type FormReport,
  type Measure,
  NORMAL_FORMS,
} from "@/lib/tool-kit/qualitative-text/unicode-normalize";

type Strings = typeof UNICODE_TEXT_NORMALIZER_STRINGS;

export interface NormalizerView {
  output: string;
  before: Measure;
  after: Measure;
  forms: FormReport;
  changes: ChangeReport;
}

const MAX_ROWS = 30;

function Change({
  before,
  after,
  strings,
}: {
  before: Measure;
  after: Measure;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <StatGrid>
      <StatCard
        label={strings.statCodePoints}
        value={t(strings.arrow, {
          before: fmt(before.codePoints),
          after: fmt(after.codePoints),
        })}
      />
      <StatCard
        label={strings.statUnits}
        value={t(strings.arrow, {
          before: fmt(before.utf16Units),
          after: fmt(after.utf16Units),
        })}
      />
      <StatCard
        emphasis
        label={strings.statBytes}
        value={t(strings.arrow, {
          before: fmt(before.utf8Bytes),
          after: fmt(after.utf8Bytes),
        })}
      />
    </StatGrid>
  );
}

function ChangesTable({
  changes,
  strings,
}: {
  changes: ChangeReport;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const shown = changes.rows.slice(0, MAX_ROWS);
  const hidden = changes.rows.length - shown.length;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>
        {tPlural(strings.changesHeading, locale, changes.changedPieces, {
          count: fmt(changes.changedPieces),
        })}
      </h3>
      <DataTable
        head={[
          strings.colBefore,
          strings.colBeforeCodes,
          strings.colAfter,
          strings.colAfterCodes,
          strings.colCount,
          strings.colBytes,
        ]}
        rows={shown.map((row) => [
          row.before,
          codePointList(row.before),
          row.after,
          codePointList(row.after),
          fmt(row.count),
          fmt(row.byteDelta, { signDisplay: "exceptZero" }),
        ])}
      />
      {hidden > 0 ? (
        <p className={HINT}>{t(strings.moreChanges, { count: fmt(hidden) })}</p>
      ) : null}
    </div>
  );
}

export function UnicodeNormalizerResults({
  view,
  strings,
}: {
  view: NormalizerView;
  strings: Strings;
}) {
  const inForms = NORMAL_FORMS.filter((form) => view.forms[form]);
  const unchanged = view.changes.changedPieces === 0;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <p className={HINT}>
        {inForms.length === 0
          ? strings.inNoForm
          : t(strings.inForms, { forms: inForms.join(", ") })}
      </p>
      <Change after={view.after} before={view.before} strings={strings} />
      {unchanged ? <Notice>{strings.unchanged}</Notice> : null}
      <div className="space-y-2">
        <label className={LABEL} htmlFor="un-output">
          {strings.outputLabel}
        </label>
        <textarea
          className={TEXTAREA}
          id="un-output"
          readOnly
          rows={8}
          value={view.output}
        />
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={view.output}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => downloadText(strings.filename, view.output)}
            type="button"
          >
            {strings.download}
          </button>
        </div>
      </div>
      {unchanged ? null : (
        <ChangesTable changes={view.changes} strings={strings} />
      )}
    </section>
  );
}
